import { RiskPage } from "source/models/risk/riskPage";
import { RiskPageData } from "source/models/risk/riskPageData";
import { UnitNodePermissions } from "source/models/unit/unitNodePermissions";
import { AbortDictionary, getApiCall, postApiCall, postFileApiCall } from "./tokenService";
import { ApiSuccess } from "ui/Scripts/source/models/common/apiSuccess";
import { ModuleUnits } from "source/models/unit/moduleUnits";
import { UnitModuleSaveResult } from "source/models/unit/unitModuleSaveResult";
import { RiskClassificationNodes } from "ui/Scripts/source/models/risk/riskClassificationNodes";
import { RiskCategoryEdit } from "source/models/riskCategory/riskCategoryEdit";
import { RiskProcessEdit } from "source/models/riskProcess/riskProcessEdit";
import { SaveSuccess } from "ui/Scripts/source/models/common/saveSuccess";
import { RiskSettingEdit } from "source/models/riskSetting/riskSettingEdit";
import { RiskStandardEdit } from "source/models/riskStandard/riskStandardEdit";
import { RiskStakeholderEdit } from "source/models/riskStakeholder/riskStakeholderEdit";
import { RiskListItems } from "ui/Scripts/source/models/risk/riskListItems";
// import { RiskNew } from "ui/Scripts/source/models/risk/riskNew";
// import { RiskNewUnitChange } from "ui/Scripts/source/models/risk/riskNewUnitChange";
// import { RiskNewRiskTemplateChange } from "ui/Scripts/source/models/risk/riskNewRiskTemplateChange";
import { RiskTemplateListItems } from "ui/Scripts/source/models/riskTemplate/riskTemplateListItems";
import { RiskTemplateEdit } from "ui/Scripts/source/models/riskTemplate/riskTemplateEdit";
import { RiskTemplateNew } from "ui/Scripts/source/models/riskTemplate/riskTemplateNew";
import { RiskEdit } from "ui/Scripts/source/models/risk/riskEdit";
import { RiskEditUnitChange } from "ui/Scripts/source/models/risk/riskEditUnitChange";
import { RiskAssessmentListItems } from "ui/Scripts/source/models/riskAssessment/riskAssessmentListItems";
import { RiskAssessmentEdit } from "ui/Scripts/source/models/riskAssessment/riskAssessmentEdit";
import { RiskAssessmentEditItems } from "ui/Scripts/source/models/riskAssessment/riskAssessmentEditItems";
import { UserListItems } from "../user/userListItems";
import { SaveSuccessItems } from "ui/Scripts/source/models/common/saveSuccessItems";
import { Attachments } from "ui/Scripts/source/models/attachment/attachments";
import { ReportItems } from "../report/reportItems";
import { RiskTypings } from "ui/Scripts/source/models/risk/riskTypings";

//Abort
const commonAborts = new AbortDictionary();
const reportAborts = new AbortDictionary();
const chartAborts = new AbortDictionary();
const statisticAborts = new AbortDictionary();

// #region RiskPage
export const getRiskPage = (): Promise<RiskPage> => {
    return getApiCall("api/risk", RiskPage);
};

export const getRiskPageData = (companyId: string): Promise<RiskPageData> => {
    return getApiCall("api/risk/pagedata?id=" + companyId, RiskPageData);
};
// #endregion RiskPage

// #region Report
export const getRiskPageReportItems = (companyId: string): Promise<ReportItems> => {
    const operationId = "getRiskPageReportItems" + (companyId ?? "");
    commonAborts.abort(operationId);
    return getApiCall("api/risk/reports?id=" + (companyId ?? ""), ReportItems, (id: string) => {
        commonAborts.add(operationId, id);
    }).finally(() => {
        commonAborts.remove(operationId);
    });
};

export const getRiskReportPrint = (companyId: string, title: string, html: string): Promise<string> => {
    const operationId = "getRiskReportPrint" + (companyId ?? "");
    commonAborts.abort(operationId);
    return postFileApiCall("api/risk/report/print", JSON.stringify({
        companyId,
        title,
        html
    }), (id: string) => {
        commonAborts.add(operationId, id);
    }).finally(() => {
        commonAborts.remove(operationId);
    });
};

export const getRiskTypings = (companyId: string, ids: string[]): Promise<RiskTypings> => {
    return postApiCall("api/risk/typings", RiskTypings, JSON.stringify({
        companyId,
        ids
    }));
};

// export const getReportGenerated = (companyId: string, reportId: string, reportName: string, employeeTypes: number[],
//     jobTitles: string[], unitIds: string[], activityState: number, parameterValues: IReportParameterValue[], printRows: number[] = []): Promise<ReportGenerated2> => {
//     reportAborts.abortAll();
//     return postApiCall("api/record/createreport", ReportGenerated2, JSON.stringify({
//         companyId,
//         reportId,
//         reportName,
//         employeeTypes,
//         jobTitles,
//         unitIds,
//         activityState,
//         parameterValues,
//         printRows
//     }), (id: string) => {
//         reportAborts.add(reportId, id);
//     }).finally(() => {
//         reportAborts.remove(reportId);
//     });
// };
// #endregion Report

// #region List
export const sendRiskReadReceipt = (companyId: string, id: string): Promise<ApiSuccess> => {
    return postApiCall("api/risk/sendreadreceipt", ApiSuccess, JSON.stringify({
        companyId,
        id
    }));
};

export const getRiskListItems = (companyId: string, pageSize: number, page: number, thrashBin: boolean, filter: string, typeIds: string[],
    unitIds: string[], stateIds: string[], riskCategoryIds: string[], riskProcessIds: string[], riskStandardIds: string[], riskStakeholderIds: string[],
    residualRiskIds: string[], riskTreatmentStateIds: string[], handlerUserCodes: string[], owners: string[], sortColumn: string, sortOrderIsAsc: boolean): Promise<RiskListItems> => {
    reportAborts.abortAll();
    chartAborts.abortAll();
    statisticAborts.abortAll();
    return postApiCall("api/risk/list", RiskListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        thrashBin,
        filter: filter || "",
        typeIds,
        stateIds,
        unitIds,
        riskCategoryIds,
        riskProcessIds,
        riskStandardIds,
        riskStakeholderIds,
        residualRiskIds,
        riskTreatmentStateIds,
        handlerUserCodes,
        owners
    }));
};
// #endregion List

// #region New
// export const getRiskNew = (companyId: string): Promise<RiskNew> => {
//     return getApiCall("api/risk/new?id=" + companyId, RiskNew);
// };

// export const getRiskNewUnitChange = (companyId: string, unitId: string): Promise<RiskNewUnitChange> => {
//     return getApiCall("api/risk/new/unitchange?id=" + unitId + "&companyId=" + companyId, RiskNewUnitChange);
// };

// export const getRiskNewRiskTemplateChange = (companyId: string, riskTemplateId: string): Promise<RiskNewRiskTemplateChange> => {
//     return getApiCall("api/risk/new/risktemplatechange?id=" + riskTemplateId + "&companyId=" + companyId, RiskNewRiskTemplateChange);
// };
// #endregion New

// #region Edit
export const getRiskEdit = (companyId: string, riskId: string, riskTemplateId: string, lockRisk: boolean): Promise<RiskEdit> => {
    return getApiCall("api/risk/edit?id=" + riskId + "&companyId=" + companyId + "&riskTemplateId=" + riskTemplateId + "&lockRisk=" + lockRisk, RiskEdit);
};

export const getRiskEditUnitChange = (companyId: string, unitId: string): Promise<RiskEditUnitChange> => {
    return getApiCall("api/risk/edit/unitchange?id=" + unitId + "&companyId=" + companyId, RiskEditUnitChange);
};

export const saveRiskEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/risk/save", SaveSuccess, JSON.stringify(obj));
};

export const removeRisk = (companyId: string, riskId: string): Promise<ApiSuccess> => {
    return postApiCall("api/risk/remove?id=" + riskId + "&companyId=" + companyId, ApiSuccess);
};

export const downloadInfoDocument = (id: string): Promise<string> => {
    return getApiCall<string>("api/risk/downloadinfo?id=" + id, null);
};
// #endregion Edit

// #region Edit Attachments
export const getRiskAttachments = (companyId: string, riskId: string): Promise<Attachments> => {
    return getApiCall("api/risk/attachments?id=" + riskId + "&companyId=" + companyId, Attachments);
};

export const riskRemoveAttachments = (data: any): Promise<ApiSuccess> => {
    return postApiCall("api/risk/editremoveattachments", ApiSuccess, JSON.stringify(data));
};

export const riskAddAttachments = (data: any): Promise<SaveSuccessItems> => {
    return postApiCall("api/risk/editaddattachments", SaveSuccessItems, JSON.stringify(data));
};
// #endregion Edit Attachments

// #region Publishing
export const publishRisk = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/risk/publish", SaveSuccess, JSON.stringify(obj));
};

export const unpublishRisk = (companyId: string, riskId: string, rowId: string): Promise<SaveSuccess> => {
    return postApiCall("api/risk/unpublish", SaveSuccess, JSON.stringify({
        companyId,
        id: riskId,
        rowId
    }));
};
// #endregion Publishing

// #region Archiving
export const archiveRisk = (companyId: string, riskId: string, rowId: string): Promise<SaveSuccess> => {
    return postApiCall("api/risk/archive", SaveSuccess, JSON.stringify({
        companyId,
        id: riskId,
        rowId
    }));
};

export const restoreRiskFromArchive = (companyId: string, riskId: string, rowId: string): Promise<SaveSuccess> => {
    return postApiCall("api/risk/restorefromarchive", SaveSuccess, JSON.stringify({
        companyId,
        id: riskId,
        rowId
    }));
};
// #endregion Archiving

// #region Trash Bin
export const emptyTrashBin = (companyId: string): Promise<ApiSuccess> => {
    return postApiCall("api/risk/emptytrash?id=" + companyId, ApiSuccess);
};

export const moveRiskToTrashBin = (companyId: string, riskId: string, rowId: string): Promise<SaveSuccess> => {
    return postApiCall("api/risk/movetotrash", SaveSuccess, JSON.stringify({
        companyId,
        id: riskId,
        rowId
    }));
};

export const restoreRiskFromTrashBin = (companyId: string, riskId: string, rowId: string): Promise<SaveSuccess> => {
    return postApiCall("api/risk/restorefromtrash", SaveSuccess, JSON.stringify({
        companyId,
        id: riskId,
        rowId
    }));
};
// #endregion Trash Bin

// #region RiskAssessments
export const sendRiskAssessmentReadReceipt = (companyId: string, id: string): Promise<ApiSuccess> => {
    return postApiCall("api/riskassesment/sendreadreceipt", ApiSuccess, JSON.stringify({
        companyId,
        id
    }));
};

export const getRiskAssessmentListItems = (companyId: string, riskId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean): Promise<RiskAssessmentListItems> => {
    return postApiCall("api/riskassesment/list", RiskAssessmentListItems, JSON.stringify({
        companyId,
        riskId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || ""
    }));
};

export const getRiskAssessmentEdit = (companyId: string, riskId: string, id: string, lockRiskAssessment: boolean): Promise<RiskAssessmentEdit> => {
    return getApiCall("api/riskassessment/edit?id=" + id + "&companyId=" + companyId + "&riskId=" + riskId + "&lockRiskAssessment=" + lockRiskAssessment, RiskAssessmentEdit);
};

export const saveRiskAssessmentEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/riskassessment/save", SaveSuccess, JSON.stringify(obj));
};

export const removeRiskAssessments = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/riskassessment/remove", ApiSuccess, JSON.stringify(obj));
};

export const getRiskAssessmentEditItems = (companyId: string, ids: string[]): Promise<RiskAssessmentEditItems> => {
    return postApiCall("api/riskassessment/editItems", RiskAssessmentEditItems, JSON.stringify({
        companyId,
        ids
    }));
};
// #endregion RiskAssessments

// #region RiskAssessment Handlers
export const getRiskAssessmentResponsiblePersonsSelectUserListItems = (companyId: string, riskId: string, unitId: string, pageSize: number, page: number, filter: string,
    ignoreUserIds: string[], ignoreUserCodes: string[], activityState: number, ignoreRecordUserConnected: boolean, doNotIgnoreRecordUserConnectedUserCode: string,
    sortColumn: string, sortOrderIsAsc: boolean): Promise<UserListItems> => {
    return postApiCall("api/riskassessment/selectriskassessmenthandlers", UserListItems, JSON.stringify({
        companyId,
        riskId,
        unitId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        ignoreUserIds,
        ignoreUserCodes,
        ignoreRecordUserConnected,
        doNotIgnoreRecordUserConnectedUserCode,
        activityState
    }));
};

export const riskAssessmentHandlersAdd = (companyId: string, riskId: string, id: string, userCodes: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/riskassessment/addriskassessmenthandlers", ApiSuccess, JSON.stringify({
        companyId,
        riskId,
        id,
        userCodes
    }));
};

export const riskAssessmentHandlersRemove = (companyId: string, riskId: string, id: string, userCodes: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/riskassessment/removeriskassessmenthandlers", ApiSuccess, JSON.stringify({
        companyId,
        riskId,
        id,
        userCodes
    }));
};

export const riskAssessmentHandlersApprove = (companyId: string, riskId: string, id: string, userCodes: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/riskassessment/approveriskassessmenthandlers", ApiSuccess, JSON.stringify({
        companyId,
        riskId,
        id,
        userCodes
    }));
};
// #endregion RiskAssessment Handlers

// #region Module Permissions
export const getUnitNodePermissions = (companyId: string): Promise<UnitNodePermissions> => {
    return getApiCall("api/risk/module/permissions?id=" + companyId, UnitNodePermissions);
};

export const saveUnitNodePermissions = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/risk/module/savepermissions", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module Permissions

// #region Module units
export const getUnits = (companyId: string): Promise<ModuleUnits> => {
    return getApiCall("api/risk/module/units?id=" + companyId, ModuleUnits);
};

export const saveUnits = (obj: any): Promise<UnitModuleSaveResult> => {
    return postApiCall("api/risk/module/saveunits", UnitModuleSaveResult, JSON.stringify(obj));
};
// #endregion Module units

// #region Module RiskSetting
export const getRiskSettings = (companyId: string): Promise<RiskSettingEdit> => {
    return getApiCall("api/risk/module/settings/edit?id=" + companyId, RiskSettingEdit);
};

export const saveRiskSettings = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/risk/module/settings/save", SaveSuccess, JSON.stringify(obj));
};
// #endregion Module RiskSetting

// #region Module risk categories
export const getRiskCategoryRiskClassificationNodes = (companyId: string): Promise<RiskClassificationNodes> => {
    return getApiCall("api/risk/module/riskcategory/nodes?id=" + (companyId ?? ""), RiskClassificationNodes);
};

export const getRiskCategoryEdit = (companyId: string, id: string): Promise<RiskCategoryEdit> => {
    return getApiCall("api/risk/module/riskcategory/edit?id=" + id + "&companyId=" + companyId, RiskCategoryEdit);
};

export const saveRiskCategoryEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/risk/module/riskcategory/save", SaveSuccess, JSON.stringify(obj));
};

export const removeRiskCategories = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/risk/module/riskcategory/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module risk categories

// #region Module risk processes
export const getRiskProcessRiskClassificationNodes = (companyId: string): Promise<RiskClassificationNodes> => {
    return getApiCall("api/risk/module/riskprocess/nodes?id=" + (companyId ?? ""), RiskClassificationNodes);
};

export const getRiskProcessEdit = (companyId: string, id: string): Promise<RiskProcessEdit> => {
    return getApiCall("api/risk/module/riskprocess/edit?id=" + id + "&companyId=" + companyId, RiskProcessEdit);
};

export const saveRiskProcessEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/risk/module/riskprocess/save", SaveSuccess, JSON.stringify(obj));
};

export const removeRiskProcesses = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/risk/module/riskprocess/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module risk processes

// #region Module risk standards
export const getRiskStandardRiskClassificationNodes = (companyId: string): Promise<RiskClassificationNodes> => {
    return getApiCall("api/risk/module/riskstandard/nodes?id=" + (companyId ?? ""), RiskClassificationNodes);
};

export const getRiskStandardEdit = (companyId: string, id: string): Promise<RiskStandardEdit> => {
    return getApiCall("api/risk/module/riskstandard/edit?id=" + id + "&companyId=" + companyId, RiskStandardEdit);
};

export const saveRiskStandardEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/risk/module/riskstandard/save", SaveSuccess, JSON.stringify(obj));
};

export const removeRiskStandards = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/risk/module/riskstandard/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module risk standards

// #region Module risk stakeholders
export const getRiskStakeholderRiskClassificationNodes = (companyId: string): Promise<RiskClassificationNodes> => {
    return getApiCall("api/risk/module/riskstakeholder/nodes?id=" + (companyId ?? ""), RiskClassificationNodes);
};

export const getRiskStakeholderEdit = (companyId: string, id: string): Promise<RiskStakeholderEdit> => {
    return getApiCall("api/risk/module/riskstakeholder/edit?id=" + id + "&companyId=" + companyId, RiskStakeholderEdit);
};

export const saveRiskStakeholderEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/risk/module/riskstakeholder/save", SaveSuccess, JSON.stringify(obj));
};

export const removeRiskStakeholders = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/risk/module/riskstakeholder/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module risk stakeholders

// #region Module risk templates
export const getRiskTemplateListItems = (companyId: string, pageSize: number, page: number, filter: string, sortColumn: string, sortOrderIsAsc: boolean, ignoreReportTemplatesOfCompanyId: string): Promise<RiskTemplateListItems> => {
    return postApiCall("api/risk/module/risktemplate/list", RiskTemplateListItems, JSON.stringify({
        companyId,
        pageSize,
        page,
        sortColumn,
        sortOrder: sortOrderIsAsc ? "asc" : "desc",
        filter: filter || "",
        ignoreReportTemplatesOfCompanyId
    }));
};

export const getRiskTemplateNew = (companyId: string): Promise<RiskTemplateNew> => {
    return getApiCall("api/risk/module/risktemplate/new?id=" + companyId, RiskTemplateNew);
};

export const getRiskTemplateEdit = (companyId: string, id: string, addToCompany: boolean): Promise<RiskTemplateEdit> => {
    return getApiCall("api/risk/module/risktemplate/edit?id=" + id + "&companyId=" + companyId + "&addToCompany=" + addToCompany, RiskTemplateEdit);
};

export const saveRiskTemplateEdit = (obj: any): Promise<SaveSuccess> => {
    return postApiCall("api/risk/module/risktemplate/save", SaveSuccess, JSON.stringify(obj));
};

export const removeRiskTemplates = (obj: any): Promise<ApiSuccess> => {
    return postApiCall("api/risk/module/risktemplate/remove", ApiSuccess, JSON.stringify(obj));
};
// #endregion Module risk templates

// #region Module risk template units
export const riskTemplateAddUnits = (companyId: string, riskTemplateId: string, unitIds: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/risk/module/risktemplate/addunits", ApiSuccess, JSON.stringify({
        companyId,
        riskTemplateId,
        unitIds
    }));
};

export const riskTemplateRemoveUnits = (companyId: string, riskTemplateId: string, unitIds: string[]): Promise<ApiSuccess> => {
    return postApiCall("api/risk/module/risktemplate/removeunits", ApiSuccess, JSON.stringify({
        companyId,
        riskTemplateId,
        unitIds
    }));
};
// #endregion Module risk template units